import { SoAPool } from './SoAPool';

/* ═══════════════════════════════════════════════════════════════════════════
   Les OBSTACLES — du décor COLLISIONNABLE qui descend avec le défilement.

   Un obstacle n'est pas un ennemi : il ne tire pas, ne pilote pas de
   trajectoire, ne rapporte pas d'XP. C'est un morceau de terrain qui glisse vers
   le joueur (voir `obstacleSystem`), qui BLOQUE ses tirs et le BLESSE au contact.
   Le joueur le détruit ou l'esquive.

   Structure-of-Arrays, calquée sur `EnemyPool` via `SoAPool` : une COLONNE par
   attribut, jamais une ligne par entité, pour que la boucle de collision ne
   traîne pas toute l'entité dans la ligne de cache quand elle ne lit que x/y, et
   pour que le coût d'allocation reste NUL après la construction.
   ═══════════════════════════════════════════════════════════════════════════ */

export class ObstaclePool {
  readonly slots: SoAPool;

  readonly x: Float32Array;
  readonly y: Float32Array;
  /** Position d'avant-tick, pour la collision BALAYÉE. À haute vitesse de
   *  défilement un obstacle franchit plusieurs fois son propre rayon en un tick :
   *  sans le segment (px,py)→(x,y), un test ponctuel le laisserait traverser le
   *  joueur sans jamais le toucher — le même tunnel que les projectiles. */
  readonly px: Float32Array;
  readonly py: Float32Array;
  /** Vitesse PROPRE, en plus du défilement commun. Nulle sur un obstacle ordinaire
   *  (il ne fait que descendre) ; non nulle sur un FRAGMENT issu d'un brisement,
   *  qui s'écarte du point d'impact avant que l'amortissement ne le rende au
   *  défilement (voir `obstacleSystem`). Deux colonnes, comme partout : la boucle
   *  de collision n'en paie pas la lecture. */
  readonly vx: Float32Array;
  readonly vy: Float32Array;
  /** Rayon de collision, en unités. Contrairement à l'ennemi (dont le `r` stocké
   *  vaut 120 % du visuel), le rayon d'un obstacle EST son rayon physique : rien
   *  à corriger côté collision, ni pour les tirs ni pour le contact. */
  readonly r: Float32Array;
  /** PV restants. Sur un obstacle INDESTRUCTIBLE (`destructible = 0`) ce champ
   *  n'est jamais décrémenté : la collision ignore les dégâts. Il reste stocké
   *  pour que la vue puisse, si elle le veut, en tirer un ratio d'usure. */
  readonly hp: Float32Array;
  /** PV à la naissance. La vue en tire un ratio (fissures, teinte) ; sans lui
   *  elle ne pourrait pas montrer qu'un rocher s'entame. */
  readonly maxHp: Float32Array;
  /** Type d'obstacle = index de sa définition dans `defs.obstacles`. Même
   *  convention que `EnemyPool.kind` : le pool stocke un entier, la vue résout la
   *  silhouette. */
  readonly type: Uint8Array;
  /** 1 = destructible (les tirs l'entament, il se brise à 0 PV) ; 0 = rocher
   *  INDESTRUCTIBLE, à PV infinis, qu'on ne peut que contourner.
   *
   *  Un drapeau et non « hp = +∞ » : un infini flottant dans un tableau typé
   *  serait un piège à NaN à la première soustraction, et l'empreinte de replay
   *  n'aime pas les non-nombres. Un booléen entier se hache proprement et rend
   *  l'indestructibilité EXPLICITE là où elle est lue. */
  readonly destructible: Uint8Array;
  /** Âge en secondes. Sert à l'ONDULATION d'une plateforme volante (phase du
   *  sinus), calculée sans état partagé. Reste inerte sur un obstacle immobile. */
  readonly age: Float32Array;
  /** Identifiant monotone, comme `EnemyPool.uid` : un ennemi qui CHEVAUCHE cette
   *  plateforme vérifie que le slot est toujours CELLE-CI et non un obstacle
   *  recyclé par accident. Sans lui, un chevaucheur se collerait à n'importe quel
   *  obstacle réoccupant le slot. */
  readonly uid: Int32Array;

  private nextUid = 1;

  constructor(capacity: number) {
    this.slots = new SoAPool(capacity);
    this.x = new Float32Array(capacity);
    this.y = new Float32Array(capacity);
    this.px = new Float32Array(capacity);
    this.py = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.r = new Float32Array(capacity);
    this.hp = new Float32Array(capacity);
    this.maxHp = new Float32Array(capacity);
    this.type = new Uint8Array(capacity);
    this.destructible = new Uint8Array(capacity);
    this.age = new Float32Array(capacity);
    this.uid = new Int32Array(capacity);
  }

  get count(): number {
    return this.slots.count;
  }
  at(i: number): number {
    return this.slots.at(i);
  }

  /** Fait naître un obstacle. Retourne le slot, ou -1 si le pool est saturé —
   *  refus silencieux côté pool, à l'appelant de le comptabiliser (World.overflow).
   *
   *  `destructible` est un booléen porté par un nombre (0/1) : le pool ne connaît
   *  pas les définitions de contenu, il reçoit des valeurs déjà résolues. */
  spawn(
    type: number,
    x: number,
    y: number,
    r: number,
    hp: number,
    destructible: number,
    vx = 0,
    vy = 0,
  ): number {
    const s = this.slots.alloc();
    if (s < 0) return -1;
    this.x[s] = x;
    this.y[s] = y;
    // px/py = x/y à la naissance : le segment balayé du premier tick est
    // dégénéré, ce qui est correct — l'obstacle n'a encore rien parcouru.
    // L'oublier ferait naître un segment fantôme depuis la position du précédent
    // occupant du slot, et donc des blocages ou des touches inexpliqués.
    this.px[s] = x;
    this.py[s] = y;
    // Réécrites à chaque naissance : un fragment recyclé qui garderait la vitesse
    // de son prédécesseur partirait en biais sans raison.
    this.vx[s] = vx;
    this.vy[s] = vy;
    this.r[s] = r;
    this.hp[s] = hp;
    this.maxHp[s] = hp;
    this.type[s] = type;
    // Réécrit à chaque naissance : un slot recyclé qui garderait le drapeau de
    // son prédécesseur ferait, une fois sur mille et seulement en partie longue,
    // un petit astéroïde soudain indestructible — ou l'inverse.
    this.destructible[s] = destructible;
    // Un slot recyclé qui garderait l'âge de son prédécesseur ferait naître une
    // plateforme en plein milieu de son ondulation, décalée sans raison.
    this.age[s] = 0;
    this.uid[s] = this.nextUid++;
    return s;
  }

  free(slot: number): void {
    this.slots.free(slot);
  }
  clear(): void {
    this.slots.clear();
  }

  /** Recopie x→px pour tout le monde, avant l'intégration du tick. */
  savePrev(): void {
    const n = this.slots.count;
    for (let i = 0; i < n; i++) {
      const s = this.slots.at(i);
      this.px[s] = this.x[s]!;
      this.py[s] = this.y[s]!;
    }
  }
}
